import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  // private readonly CHECK_INTERVAL_MS = 3_600_000; // 1h
  private readonly CHECK_INTERVAL_MS = 600_000; // 10min
  private intervalId?: ReturnType<typeof setInterval>;

  private readonly _isUpdateReady = signal<boolean>(false);
  public readonly isUpdateReady = this._isUpdateReady.asReadonly();

  constructor() {
    this.initializeUpdateListener();
    this.setupPeriodicCheck();
  }

  private initializeUpdateListener(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this._isUpdateReady.set(true);
      });
  }

  private setupPeriodicCheck(): void {
    if (!this.swUpdate.isEnabled) return;

    // outside angular zone
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(async () => {
        try {
          await this.swUpdate.checkForUpdate();
        } catch (error) {
          console.error('Error in the periodic PWA check:', error);
        }
      }, this.CHECK_INTERVAL_MS);
    });

    this.destroyRef.onDestroy(() => {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    });
  }

  public async activateUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) return false;

    try {
      return await this.swUpdate.activateUpdate();
    } catch (error) {
      console.error('Error activating PWA update:', error);
      return false;
    }
  }
}
